// Imports
import { faLocationDot, faRoad } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components/macro';
import Input from '../../foundation/front-end/components/forms/fields/input';
import PlaceUpserter, { Address } from '.';
import { OnPlaceChanged, Regions } from './utils/autocomplete/types';
import HighlightedText from './utils/autocomplete/highlighted-text';
import useApiAutocomplete from './utils/autocomplete/use-api-autocomplete';


// Styled components
const Container = styled.div`
	position: relative;
`;

const Listbox = styled.ul`
	position: absolute;
	top: calc(100% + 0.0625rem);
	left: 0;
	right: 0;
	margin: 0;
	background: #fff;
	border: 0.0625rem solid #ddd;
	border-radius: 0.2em;
	max-height: 15.125rem;
	overflow-y: auto;
	z-index: 2;
	cursor: pointer;
`;

const OptionItem = styled.li<{ active: boolean }>`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.5625rem 1rem;
	font-size: 0.875rem;
	background: ${({ active }) => (active ? '#f1f2f3' : 'transparent')};
	transition: background 0.1s;
	
	&:not(:last-child) {
		border-bottom: 0.0625rem solid #ddd;
	}
`;

const OptionText = styled.span`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;


// Define the accepted props
interface Props
	extends Pick<
		React.ComponentProps<typeof Input>,
		'name' | 'initialValue' | 'note' | 'exclude' | 'disabled' | 'onChange' | 'autoFocus' | 'errorMessage'
	> {
	fallbackCountry: React.ComponentProps<typeof PlaceUpserter>['fallbackCountry'];
	regions: Regions;
	threeLetterCountryCode?: Address['countryCode'];
	onPlaceChanged: OnPlaceChanged;
	type?: Address['type'];
	required?: boolean;
	autocompleteDisabled?: boolean;
}


// Function component
const StreetAddressInput: React.FC<Props> = ({
	threeLetterCountryCode,
	fallbackCountry,
	type,
	regions,
	required,
	onPlaceChanged,
	autocompleteDisabled,
	...props
}) => {
	// Use state
	const [streetAddressValue, setStreetAddressValue] = useState(props.initialValue ?? '');
	
	
	// Use ref
	const rawSetterRef = useRef<(value: string) => void>();
	const streetAddressSetter = useRef<(value: string) => void>();
	
	
	// Sync when initial value loads asynchronously
	useEffect(() => {
		if (props.initialValue !== undefined) {
			setStreetAddressValue(props.initialValue);
		}
	}, [props.initialValue]);
	
	
	// Wrap the raw setter so that any programmatic update also syncs our tracked value
	const bindSetter = useCallback((setter: (value: string) => void) => {
		rawSetterRef.current = setter;
		
		streetAddressSetter.current = (value: string) => {
			setter(value);
			setStreetAddressValue(value);
		};
	}, []);
	
	
	// Determine three letter country code
	const effectiveThreeLetterCountryCode = useMemo(() => {
		if (threeLetterCountryCode) {
			return threeLetterCountryCode;
		}
		
		if (fallbackCountry) {
			return regions?.find((region) => region.twoLetterCode.toLowerCase() === fallbackCountry.toLowerCase())?.code;
		}
	}, [threeLetterCountryCode, fallbackCountry, regions]);
	
	
	// Use API autocomplete
	const {
		suggestions,
		isOpen,
		activeIndex,
		containerRef,
		listboxRef,
		setActiveIndex,
		handleSuggestionSelect,
		handleKeyDown,
		handleFocus,
	} = useApiAutocomplete({
		disabled: autocompleteDisabled,
		type: type,
		effectiveThreeLetterCountryCode: effectiveThreeLetterCountryCode,
		regions: regions,
		onPlaceChanged: onPlaceChanged,
		streetAddressSetter: streetAddressSetter,
		streetAddressValue: streetAddressValue,
	});
	
	
	// Return JSX
	return (
		<Container ref={containerRef}>
			<Input
				type='text'
				label='Street address'
				icon={faRoad}
				maxLength={100}
				bindSetter={bindSetter}
				onKeyDown={handleKeyDown}
				onFocus={handleFocus}
				{...props}
				onChange={(newValue) => {
					setStreetAddressValue(newValue);
					props.onChange?.(newValue);
				}}
				autoComplete={autocompleteDisabled ? undefined : 'off'}
				aria-autocomplete={autocompleteDisabled ? undefined : 'list'}
				aria-expanded={autocompleteDisabled ? undefined : isOpen}
				required={required}
			/>
			
			{isOpen && suggestions.length > 0 && (
				<Listbox ref={listboxRef} role='listbox' aria-label='Address suggestions'>
					{suggestions.map((suggestion, index) => {
						// Initialize
						const [formattedStreetAddress, formattedLocality = ''] = suggestion.formatted.split('\n');
						
						
						// Return JSX
						return (
							<OptionItem
								key={`${suggestion.formatted}-${index}`}
								role='option'
								aria-selected={index === activeIndex}
								active={index === activeIndex}
								onClick={() => handleSuggestionSelect(suggestion)}
								onMouseEnter={() => setActiveIndex(index)}
							>
								<FontAwesomeIcon icon={faLocationDot} color='#9ca3af' />
								<OptionText>
									<span>
										<HighlightedText fullText={formattedStreetAddress} query={streetAddressValue} />{' '}
										<small>
											<HighlightedText fullText={formattedLocality} query={streetAddressValue} />
										</small>
									</span>
								</OptionText>
							</OptionItem>
						);
					})}
				</Listbox>
			)}
		</Container>
	);
};

export default StreetAddressInput;
